import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Typography,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  CircularProgress,
  Pagination,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Home,
  Business,
} from '@mui/icons-material';
import api from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { exportToExcel, exportToPDF } from '../../utils/exportHelpers';
import { useLanguage } from '../../contexts/LanguageContext';
import { extractAndTranslateError, formatNumber, formatStringWithNumbers } from '../../utils/i18nHelpers';

interface Building {
  id: number;
  name: string;
  name_ar?: string;
  building_code: string;
  total_floors?: number;
  total_flats?: number;
  amenities?: number[] | string[];
  amenities_names?: Array<{ id: number; name_en: string; name_ar: string }>;
}

interface Flat {
  id: number;
  flat_number: string;
  floor_number: string;
  status: 'vacant' | 'occupied' | 'under_maintenance' | 'vacant_ready_to_rent';
  description?: string;
  is_active: boolean;
  building?: Building;
}

const Flats: React.FC = () => {
  const { can } = useAuth();
  const { notifySuccess, notifyError } = useNotification();
  const { t, language } = useLanguage();
  const [flats, setFlats] = useState<Flat[]>([]);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [editingFlat, setEditingFlat] = useState<Flat | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filterBuilding, setFilterBuilding] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<string>('asc');
  const [formData, setFormData] = useState({
    building_id: '',
    flat_number: '',
    floor_number: '1' as string,
    status: 'vacant' as 'vacant' | 'occupied' | 'under_maintenance' | 'vacant_ready_to_rent',
    description: '',
  });

  useEffect(() => {
    fetchBuildings();
  }, []);

  // Debounce search term to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1); // Reset to page 1 when search changes
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchFlats();
  }, [page, searchTerm, filterBuilding, filterStatus, sortOrder]);

  const fetchFlats = async () => {
    try {
      setLoading(true);
      const params: any = {
        page,
        per_page: perPage,
      };
      
      if (searchTerm) {
        params.search = searchTerm;
      }
      
      if (filterBuilding) {
        params.building_id = filterBuilding;
      }
      
      if (filterStatus) {
        params.status = filterStatus;
      }

      if (sortOrder) {
        params.sort_order = sortOrder;
      }

      const response = await api.get('/flats', { params });

      // Handle paginated response
      if (response.data.data) {
        // Laravel pagination format
        setFlats(Array.isArray(response.data.data) ? response.data.data : []);
        setTotalPages(response.data.last_page || 1);
        setTotalItems(response.data.total || 0);
      } else if (Array.isArray(response.data)) {
        // Fallback for non-paginated response
        setFlats(response.data);
        setTotalPages(1);
        setTotalItems(response.data.length);
      } else {
        setFlats([]);
        setTotalPages(1);
        setTotalItems(0);
      }
    } catch (error) {
      console.error('Error fetching flats:', error);
      setFlats([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchBuildings = async () => {
    try {
      const response = await api.get('/buildings');
      setBuildings(response.data);
    } catch (error) {
      console.error('Error fetching buildings:', error);
    }
  };

  const handleOpen = (flat?: Flat) => {
    if (flat) {
      setEditingFlat(flat);
      setFormData({
        building_id: flat.building?.id?.toString() || '',
        flat_number: flat.flat_number,
        floor_number: String(flat.floor_number),
        status: (flat.status as string) === 'available' ? 'vacant' : ((flat.status as string) === 'maintenance' ? 'under_maintenance' : flat.status),
        description: flat.description || '',
      });
    } else {
      setEditingFlat(null);
      setFormData({
        building_id: '',
        flat_number: '',
        floor_number: '1',
        status: 'vacant',
        description: '',
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingFlat(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return; // Prevent double submission
    setSubmitting(true);
    try {
      const buildingId = parseInt(formData.building_id);
      const selectedBuilding = buildings.find(b => b.id === buildingId);

      // Check if building has reached its total_flats limit (only for new flats)
      if (!editingFlat && selectedBuilding && selectedBuilding.total_flats) {
        const currentFlatsCount = flats.filter(f => f.building?.id === buildingId).length;
        if (currentFlatsCount >= selectedBuilding.total_flats) {
          notifyError(
            language === 'ar' 
              ? `وصل المبنى إلى الحد الأقصى لعدد الشقق (${selectedBuilding.total_flats}). لا يمكن إنشاء المزيد من الشقق.`
              : `The building has reached its maximum number of flats (${selectedBuilding.total_flats}). Cannot create more flats.`
          );
          setSubmitting(false);
          return;
        }
      }

      // Check if moving flat to a different building would exceed the new building's limit
      if (editingFlat && selectedBuilding && selectedBuilding.total_flats) {
        const newBuildingId = buildingId;
        const oldBuildingId = editingFlat.building?.id;
        
        // Only check if building is being changed
        if (oldBuildingId && newBuildingId !== oldBuildingId) {
          const currentFlatsCount = flats.filter(f => f.building?.id === newBuildingId).length;
          if (currentFlatsCount >= selectedBuilding.total_flats) {
            notifyError(
              language === 'ar'
                ? `وصل المبنى المحدد إلى الحد الأقصى لعدد الشقق (${selectedBuilding.total_flats}). لا يمكن نقل هذه الشقة إلى هذا المبنى.`
                : `The selected building has reached its maximum number of flats (${selectedBuilding.total_flats}). Cannot move this flat to this building.`
            );
            setSubmitting(false);
            return;
          }
        }
      }

      const submitData = {
        building_id: buildingId,
        flat_number: formData.flat_number,
        floor_number: formData.floor_number,
        status: formData.status,
        description: formData.description || null,
      };

      if (editingFlat) {
        await api.put(`/flats/${editingFlat.id}`, submitData);
        notifySuccess(t('flats.messages.save.successUpdate'));
      } else {
        await api.post('/flats', submitData);
        notifySuccess(t('flats.messages.save.successCreate'));
      }
      // Refresh current page after add/edit
      await fetchFlats();
      handleClose();
    } catch (error: any) {
      console.error('Error saving flat:', error);
      
      const errorMessage = extractAndTranslateError(error, language, t);
      notifyError(errorMessage || t('flats.messages.save.errorDefault'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm(t('flats.messages.delete.confirm'))) {
      try {
        await api.delete(`/flats/${id}`);
        fetchFlats();
        notifySuccess(t('flats.messages.delete.success'));
      } catch (error) {
        console.error('Error deleting flat:', error);
        const resp = (error as any)?.response;
        const message =
          resp?.data?.error ||
          (resp?.status === 422 && t('flats.messages.delete.cannotDeleteWithRelations')) ||
          t('flats.messages.delete.errorDefault');
        notifyError(message);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // floor_number is now a string field (can be numeric or G, B, P, R)
    if (name === 'floor_number') {
      setFormData(prev => ({ ...prev, [name]: value }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'occupied': return 'success';
      case 'vacant': return 'default';
      case 'vacant_ready_to_rent': return 'info';
      case 'under_maintenance': return 'warning';
      case 'available': return 'default'; // Legacy support
      case 'maintenance': return 'warning'; // Legacy support
      default: return 'default';
    }
  };

  if (loading) {
    return <div>{t('flats.loading')}</div>;
  }

  // No need for client-side filtering since backend handles it
  const filteredFlats = flats;

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPage(1); // Reset to first page on search
  };

  const handleBuildingFilterChange = (e: any) => {
    setFilterBuilding(e.target.value);
    setPage(1); // Reset to first page on filter change
  };

  const handleStatusFilterChange = (e: any) => {
    setFilterStatus(e.target.value);
    setPage(1); // Reset to first page on filter change
  };

  const handleSortOrderChange = (e: any) => {
    setSortOrder(e.target.value);
    setPage(1); // Reset to first page on sort change
  };

  // For exports, fetch all data without pagination
  const fetchAllFlatsForExport = async () => {
    try {
      const params: any = {
        per_page: 10000, // Large number to get all
      };
      
      if (searchTerm) {
        params.search = searchTerm;
      }
      
      if (filterBuilding) {
        params.building_id = filterBuilding;
      }
      
      if (filterStatus) {
        params.status = filterStatus;
      }
      
      const response = await api.get('/flats', { params });
      
      if (response.data.data) {
        return response.data.data;
      } else if (Array.isArray(response.data)) {
        return response.data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching all flats for export:', error);
      return [];
    }
  };

  const handleExportExcel = async () => {
    try {
      const allFlats = await fetchAllFlatsForExport();
      if (!allFlats.length) {
        notifyError(t('flats.export.noData'));
        return;
      }
      const rows = allFlats.map((f: Flat) => ({
        [t('flats.export.columns.flat')]: f.flat_number,
        [t('flats.export.columns.building')]: language === 'ar' && f.building?.name_ar ? f.building.name_ar : (f.building?.name || ''),
        [t('flats.export.columns.floor')]: f.floor_number,
        [t('flats.export.columns.status')]: t(`flats.status.${f.status}`),
      }));
      await exportToExcel(rows, 'flats');
      notifySuccess(t('flats.export.excel.success'));
    } catch (error) {
      notifyError(t('flats.export.excel.error') || 'Failed to export Excel');
    }
  };

  const handleExportPDF = async () => {
    try {
      const allFlats = await fetchAllFlatsForExport();
      if (!allFlats.length) {
        notifyError(t('flats.export.noData'));
        return;
      }
      const columns = [
        t('flats.export.columns.flat'),
        t('flats.export.columns.building'),
        t('flats.export.columns.floor'),
        t('flats.export.columns.status')
      ];
      const data = allFlats.map((f: Flat) => [
        f.flat_number,
        language === 'ar' && f.building?.name_ar ? f.building.name_ar : (f.building?.name || ''),
        f.floor_number,
        t(`flats.status.${f.status}`),
      ]);
      await exportToPDF(columns, data, 'flats', language, t('flats.title'));
      notifySuccess(t('flats.export.pdf.success'));
    } catch (error) {
      notifyError(t('flats.export.pdf.error') || 'Failed to export PDF');
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h4">{t('flats.title')}</Typography>
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          <TextField
            size="small"
            placeholder={t('flats.search.placeholder')}
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>{t('flats.dialog.building.label')}</InputLabel>
            <Select
              value={filterBuilding}
              label={t('flats.dialog.building.label')}
              onChange={handleBuildingFilterChange}
            >
              <MenuItem value="">{t('flats.dialog.building.all') || 'All Buildings'}</MenuItem>
              {buildings.map((building) => (
                <MenuItem key={building.id} value={building.id.toString()}>
                  {language === 'ar' && building.name_ar ? building.name_ar : building.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>{t('flats.dialog.status.label')}</InputLabel>
            <Select
              value={filterStatus}
              label={t('flats.dialog.status.label')}
              onChange={handleStatusFilterChange}
            >
              <MenuItem value="">{t('flats.dialog.status.all') || 'All Statuses'}</MenuItem>
              <MenuItem value="occupied">{t('flats.dialog.status.occupied')}</MenuItem>
              <MenuItem value="vacant">{t('flats.dialog.status.vacant')}</MenuItem>
              <MenuItem value="vacant_ready_to_rent">{t('flats.dialog.status.vacantReadyToRent')}</MenuItem>
              <MenuItem value="under_maintenance">{t('flats.dialog.status.underMaintenance')}</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('flats.sort.label') || 'Sort Order'}</InputLabel>
            <Select
              value={sortOrder}
              label={t('flats.sort.label') || 'Sort Order'}
              onChange={handleSortOrderChange}
            >
              <MenuItem value="asc">{t('flats.sort.asc') || 'Ascending (A-Z)'}</MenuItem>
              <MenuItem value="desc">{t('flats.sort.desc') || 'Descending (Z-A)'}</MenuItem>
            </Select>
          </FormControl>
          {can('flats.export-excel') && (
            <Button variant="outlined" onClick={handleExportExcel}>
              {t('flats.export.excel')}
            </Button>
          )}
          {can('flats.export-pdf') && (
            <Button variant="outlined" onClick={handleExportPDF}>
              {t('flats.export.pdf')}
            </Button>
          )}
          {can('flats.add') && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpen()}
            >
              {t('flats.dialog.addTitle')}
            </Button>
          )}
        </Box>
      </Box>

      {loading && filteredFlats.length === 0 ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <CircularProgress />
        </Box>
      ) : filteredFlats.length === 0 ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <Typography variant="h6" color="text.secondary">
            {t('flats.noResults') || 'No flats found'}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredFlats.map((flat) => (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={flat.id}>
            <Card 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                minHeight: 300,
              }}
            >
              <CardContent sx={{ flexGrow: 1, p: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', flex: 1 }}>
                    {formatStringWithNumbers(flat.flat_number, language)}
                  </Typography>
                  <Box>
                    {can('flats.edit') && (
                      <IconButton
                        size="small"
                        onClick={() => handleOpen(flat)}
                      >
                        <Edit />
                      </IconButton>
                    )}
                    {can('flats.delete') && (
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(flat.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {t('flats.card.building')}
                    </Typography>
                    <Box display="flex" alignItems="center">
                      <Business fontSize="small" color="action" sx={{ mr: 0.5 }} />
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {flat.building 
                          ? (language === 'ar' && flat.building.name_ar ? flat.building.name_ar : flat.building.name)
                          : t('flats.card.building.na')}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {t('flats.card.floorLabel')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                      {flat.floor_number === 'G' ? (t('flats.floors.ground') || 'Ground Floor')
                        : flat.floor_number === 'B' ? (t('flats.floors.basement') || 'Basement')
                        : flat.floor_number === 'P' ? (t('flats.floors.parking') || 'Parking')
                        : flat.floor_number === 'R' ? (t('flats.floors.roof') || 'Roof')
                        : t('flats.card.floorValue').replace('{{floor}}', formatNumber(Number(flat.floor_number), language))}
                    </Typography>
                  </Box>
                  
                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Chip
                      label={
                        flat.status === 'occupied'
                          ? t('flats.card.status.occupied')
                          : flat.status === 'vacant'
                          ? t('flats.card.status.vacant')
                          : flat.status === 'vacant_ready_to_rent'
                          ? t('flats.card.status.vacantReadyToRent')
                          : t('flats.card.status.underMaintenance')
                      }
                      size="small"
                      color={getStatusColor(flat.status) as any}
                    />
                  </Box>
                  
                  {/* Maintenance charge removed as per latest requirements */}
                </Box>
              </CardContent>
            </Card>
          </Grid>
          ))}
        </Grid>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" alignItems="center" mt={4} mb={2} gap={2}>
          <Typography variant="body2" color="text.secondary">
            {t('flats.pagination.showing') || 'Showing'} {((page - 1) * perPage) + 1} - {Math.min(page * perPage, totalItems)} {t('flats.pagination.of') || 'of'} {totalItems} {t('flats.pagination.results') || 'results'}
          </Typography>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingFlat ? t('flats.dialog.editTitle') : t('flats.dialog.addTitle')}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('flats.dialog.building.label')}</InputLabel>
                  <Select
                    value={formData.building_id}
                    label={t('flats.dialog.building.label')}
                    onChange={(e) => {
                      const buildingId = e.target.value;
                      setFormData({ ...formData, building_id: buildingId });
                    }}
                    required
                  >
                    {buildings.map((building) => {
                      const buildingName = language === 'ar' && building.name_ar ? building.name_ar : (building.name || '');
                      const buildingCode = building.building_code || '';
                      // Only show code in parentheses if it exists
                      const displayText = buildingCode 
                        ? t('flats.dialog.building.option')
                            .replace('{{name}}', buildingName)
                            .replace('{{code}}', buildingCode)
                        : buildingName;
                      return (
                        <MenuItem key={building.id} value={building.id.toString()}>
                          {displayText}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label={t('flats.dialog.flatNumber')}
                  name="flat_number"
                  value={formData.flat_number}
                  onChange={handleInputChange}
                  required
                />
              </Box>
              
              <TextField
                fullWidth
                label={t('flats.dialog.floorNumber')}
                name="floor_number"
                value={formData.floor_number}
                onChange={handleInputChange}
                required
                helperText={t('flats.dialog.floorNumberHelper') || 'Enter floor number (1, 2, 3...) or special floor (G=Ground, B=Basement, P=Parking, R=Roof)'}
              />
              
              <FormControl fullWidth>
                <InputLabel>{t('flats.dialog.status.label')}</InputLabel>
                <Select
                  value={formData.status}
                  label={t('flats.dialog.status.label')}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                >
                  <MenuItem value="vacant">{t('flats.dialog.status.vacant')}</MenuItem>
                  <MenuItem value="vacant_ready_to_rent">{t('flats.dialog.status.vacantReadyToRent')}</MenuItem>
                  <MenuItem value="occupied">{t('flats.dialog.status.occupied')}</MenuItem>
                  <MenuItem value="under_maintenance">{t('flats.dialog.status.underMaintenance')}</MenuItem>
                </Select>
              </FormControl>
              
              <TextField
                fullWidth
                label={t('flats.dialog.description')}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={2}
              />
              
              {formData.building_id && (() => {
                const buildingAmenities = (() => {
                  const selectedBuilding = buildings.find(b => b.id.toString() === formData.building_id);
                  if (selectedBuilding?.amenities_names && Array.isArray(selectedBuilding.amenities_names) && selectedBuilding.amenities_names.length > 0) {
                    // Use amenities_names if available (contains name_en and name_ar)
                    return selectedBuilding.amenities_names
                      .map((amenity: { id: number; name_en: string; name_ar: string }) => 
                        language === 'ar' ? amenity.name_ar : amenity.name_en
                      )
                      .join(', ');
                  } else if (selectedBuilding?.amenities) {
                    // Fallback to amenities if amenities_names is not available
                    return Array.isArray(selectedBuilding.amenities) 
                      ? selectedBuilding.amenities.join(', ') 
                      : String(selectedBuilding.amenities);
                  }
                  return '';
                })();
                return buildingAmenities ? (
                  <TextField
                    fullWidth
                    label={t('flats.dialog.amenities')}
                    value={buildingAmenities}
                    disabled
                  />
                ) : null;
              })()}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={submitting}>{t('flats.dialog.cancel')}</Button>
            <Button type="submit" variant="contained" disabled={submitting} startIcon={submitting ? <CircularProgress size={20} /> : null}>
              {editingFlat ? t('flats.dialog.update') : t('flats.dialog.create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Flats;